/**
 * quality-score — «Разбор балла»: метрики A-F + пятибалльная разбивка
 * качества + уверенность модели. Зеркалит QualityScoreSection.tsx
 * (v2/analysis), включая гейт видимости: карточка появляется, только если
 * хотя бы одна метрика A-F — настоящее число, либо есть разбивка. Печатный
 * документ не сворачивается, поэтому вместо коллапс-шеврона в шапке карточки
 * стоит счётчик заполненных критериев (`criteriaCount`) — тот же, что и на
 * экране, а не общий балл (он уже виден крупно в hero-блоке).
 *
 * SCORE_MAP/BREAKDOWN_KEYS/confidenceLevelPercent импортированы, а не
 * скопированы — единственная причина существования этой задачи: старый
 * printReport подписывал метрику C «Предложение решения» и подставлял туда
 * clarityScore, хотя на экране C — objectionScore («Работа с
 * возражениями»). Копия списка неизбежно расходится с экраном; общий
 * источник — нет.
 */
import { card, kvRow, nullableScore, scoreSpan } from '../primitives';
import { scoreHex, scoreTint, TINT } from '../tokens';
import { escapeHtml } from '../escape';
import type { SectionFn } from '../types';
import {
  SCORE_MAP,
  BREAKDOWN_KEYS,
  confidenceLevelPercent,
} from '@/components/details/v2/analysis/analysis-utils';
import { isComponentMetric } from '@aica/core';

export const renderQualityScore: SectionFn = (input, t) => {
  const { analysis } = input;
  const componentScores = analysis.componentScores;
  const breakdown = analysis.result.summary?.qualityScoreBreakdown;

  // Тот же гейт, что и на экране (QualityScoreSection.tsx): карточка
  // появляется, только если хотя бы одна A-F метрика реально числовая — null
  // (метрика неприменима) считается так же, как отсутствие, иначе полностью
  // неоценённый звонок печатал бы шесть прочерков вместо пустой секции.
  // Когда гейт сработал, печатаются все шесть строк по порядку (пропуски и
  // null — прочерком), чтобы буквы оставались последовательными.
  const hasAnyAgScore = SCORE_MAP.some(
    ({ key }) => typeof componentScores?.[key] === 'number' && isComponentMetric(key)
  );
  const agRows = hasAnyAgScore ? SCORE_MAP : [];
  const subRows = BREAKDOWN_KEYS.filter(({ field }) => breakdown?.[field] !== undefined);

  if (agRows.length === 0 && subRows.length === 0) return '';

  const metricsHtml = agRows.length
    ? `<div class="metrics">${agRows
        .map(({ key, code, labelKey }) => {
          const val = componentScores?.[key];
          const hasVal = typeof val === 'number';
          const circleBg = hasVal ? scoreTint(val) : TINT.neutral;
          const circleColor = hasVal ? scoreHex(val) : 'var(--fg3)';
          return (
            `<div class="metric">` +
            `<span class="code" style="background:${circleBg};color:${circleColor}">${escapeHtml(code)}</span>` +
            `<span class="nm">${escapeHtml(t(labelKey))}</span>` +
            `<span class="sc">${nullableScore(val)}</span>` +
            `</div>`
          );
        })
        .join('')}</div>`
    : '';

  const breakdownHtml = subRows.length
    ? `<div class="breakdown">${subRows
        .map(
          ({ field, labelKey }) =>
            `<div class="bd"><div class="l">${escapeHtml(t(labelKey))}</div>` +
            `<div class="v">${scoreSpan(breakdown![field]!)}</div></div>`
        )
        .join('')}</div>`
    : '';

  const confidence = confidenceLevelPercent(analysis.result.meta?.confidenceLevel);
  const confidenceHtml =
    confidence !== undefined
      ? kvRow(t('records.analysisPanel.confidence'), `${confidence}%`)
      : '';

  // Badge считает только заполненные критерии (реальное число), как на
  // экране: agRows печатает все 6 букв включая приглушённые «—»-заглушки,
  // но они не должны раздувать счётчик.
  const filledAgCount = agRows.filter(
    ({ key }) => typeof componentScores?.[key] === 'number'
  ).length;
  const criteriaCount = filledAgCount + subRows.length;

  return card({
    title: t('records.analysisPanel.scoreBreakdown'),
    value: `<span style="color:var(--fg3)">${escapeHtml(t('records.analysisPanel.criteriaCount', { count: criteriaCount }))}</span>`,
    body: metricsHtml + breakdownHtml + confidenceHtml,
  });
};
